import React, { useState, useEffect } from 'react';
import { getAllTransactions, getUsers, getSubadmins } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { buildUserOptions, ROLE_LABEL } from '../../lib/userOptions';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { History, Search, Filter, X, Calendar } from 'lucide-react';

const buildServiceOptions = (transactions) =>
  [...new Set(transactions.map((t) => t.service_name))].sort(
    (a, b) => (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0)
  );

export default function TransactionsHistory() {
  const { user: me } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [transRes, usersRes, subsRes] = await Promise.all([
          getAllTransactions(),
          getUsers(),
          me?.role === 'admin' ? getSubadmins() : Promise.resolve({ data: [] }),
        ]);
        setTransactions(transRes.data);
        setFiltered(transRes.data);
        setUsers(buildUserOptions([me, ...subsRes.data, ...usersRes.data], transRes.data));

        setServices(buildServiceOptions(transRes.data));
      } catch (err) {
        console.error('Error fetching transactions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let result = [...transactions];

    // Filter by search text
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.username.toLowerCase().includes(lower) ||
          t.service_name.toLowerCase().includes(lower) ||
          t.phone_number.includes(search)
      );
    }

    // Filter by user
    if (selectedUser && selectedUser !== 'all') {
      result = result.filter((t) => t.username === selectedUser);
    }

    // Filter by service
    if (selectedService && selectedService !== 'all') {
      result = result.filter((t) => t.service_name === selectedService);
    }

    // Filter by date range
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((t) => new Date(t.created_at) >= fromDate);
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((t) => new Date(t.created_at) <= toDate);
    }

    setFiltered(result);
  }, [search, selectedUser, selectedService, dateFrom, dateTo, transactions]);

  const clearFilters = () => {
    setSearch('');
    setSelectedUser('all');
    setSelectedService('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = search || selectedUser !== 'all' || selectedService !== 'all' || dateFrom || dateTo;

  // Calculate totals
  const totals = {
    count: filtered.length,
    baseAmount: filtered.reduce((sum, t) => sum + (t.status === 'completed' ? t.base_amount : 0), 0),
    discountAmount: filtered.reduce((sum, t) => sum + (t.status === 'completed' ? t.discount_amount : 0), 0),
    finalPrice: filtered.reduce((sum, t) => sum + (t.status === 'completed' ? t.final_price : 0), 0),
  };

  // Group by service/base price
  const groupedByService = filtered.reduce((acc, tx) => {
    const key = `${tx.service_name}_${tx.base_amount}`;
    if (!acc[key]) {
      acc[key] = {
        serviceName: tx.service_name,
        basePrice: tx.base_amount,
        transactions: [],
        totals: {
          count: 0,
          baseAmount: 0,
          discountAmount: 0,
          finalPrice: 0,
        }
      };
    }
    acc[key].transactions.push(tx);
    acc[key].totals.count += 1;
    if (tx.status === 'completed') {
      acc[key].totals.baseAmount += tx.base_amount;
      acc[key].totals.discountAmount += tx.discount_amount;
      acc[key].totals.finalPrice += tx.final_price;
    }
    return acc;
  }, {});

  const serviceGroups = Object.values(groupedByService).sort((a, b) => b.basePrice - a.basePrice);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-history">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Transacciones</h1>
          <p className="text-muted-foreground mt-1">
            Historial de todas las recargas
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/40">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-primary" />
            <span className="font-medium">Filtros</span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="ml-auto text-muted-foreground hover:text-foreground"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Teléfono, servicio..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-secondary/50"
                  data-testid="search-transactions"
                />
              </div>
            </div>

            {/* User filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Usuario</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-secondary/50" data-testid="filter-user">
                  <SelectValue placeholder="Todos los usuarios" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.username} value={u.username} data-testid={`filter-user-option-${u.username}`}>
                      {u.username}
                      {u.role && <span className="ml-2 text-xs text-muted-foreground">({ROLE_LABEL[u.role]})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Servicio</Label>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="bg-secondary/50" data-testid="filter-service">
                  <SelectValue placeholder="Todos los servicios" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">Todos los servicios</SelectItem>
                  {services.map((name) => (
                    <SelectItem key={name} value={name} data-testid={`filter-service-option-${name}`}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date from */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Desde</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="pl-9 bg-secondary/50"
                  data-testid="filter-date-from"
                />
              </div>
            </div>

            {/* Date to */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Hasta</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="pl-9 bg-secondary/50"
                  data-testid="filter-date-to"
                />
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground mt-3">
              Mostrando {filtered.length} de {transactions.length} transacciones
            </p>
          )}
        </CardContent>
      </Card>

      {/* Transactions grouped by service */}
      {filtered.length === 0 ? (
        <Card className="bg-card border-border/40">
          <CardContent className="p-0">
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay transacciones</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {serviceGroups.map((group) => (
            <Card key={`${group.serviceName}_${group.basePrice}`} className="bg-card border-border/40">
              <CardContent className="p-0">
                {/* Group Header */}
                <div className="p-4 bg-secondary/30 border-b border-border/40 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-sm px-3 py-1">
                      {formatCurrency(group.basePrice)}
                    </Badge>
                    <span className="font-bold">{group.serviceName}</span>
                    <span className="text-muted-foreground">({group.totals.count} transacciones)</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-mono text-accent font-bold">{formatCurrency(group.totals.finalPrice)}</span>
                  </div>
                </div>
                
                {/* Transactions Table */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50 hover:bg-transparent">
                        <TableHead>Usuario</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Descuento</TableHead>
                        <TableHead>Precio Final</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.transactions.map((tx) => (
                        <TableRow
                          key={tx.id}
                          className="border-border/30 hover:bg-secondary/30"
                        >
                          <TableCell className="font-medium">{tx.username}</TableCell>
                          <TableCell className="font-mono">{tx.phone_number}</TableCell>
                          <TableCell>
                            <span className="text-primary font-medium">
                              -{tx.discount_percentage}%
                            </span>
                            <span className="text-xs text-muted-foreground ml-1">
                              ({formatCurrency(tx.discount_amount)})
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-accent font-bold">
                            {formatCurrency(tx.final_price)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                tx.status === 'completed'
                                  ? 'status-active'
                                  : tx.status === 'pending'
                                  ? 'status-pending'
                                  : 'status-inactive'
                              }
                            >
                              {tx.status === 'completed'
                                ? 'Completado'
                                : tx.status === 'pending'
                                ? 'Pendiente'
                                : tx.status === 'refunded'
                                ? 'Devuelta'
                                : 'Fallido'}
                            </Badge>
                            {tx.status !== 'completed' && tx.dc_error && (
                              <p className="text-xs text-destructive mt-1 max-w-[220px]" data-testid={`tx-error-${tx.id}`}>{tx.dc_error}</p>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(tx.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Group Subtotals */}
                <div className="p-3 bg-secondary/20 border-t border-border/40">
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <span className="text-muted-foreground">Cantidad:</span>
                      <span className="font-bold ml-2">{group.totals.count}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-muted-foreground">Base:</span>
                      <span className="font-mono ml-2">{formatCurrency(group.totals.baseAmount)}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-muted-foreground">Descuentos:</span>
                      <span className="font-mono text-primary ml-2">-{formatCurrency(group.totals.discountAmount)}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-mono text-accent font-bold ml-2">{formatCurrency(group.totals.finalPrice)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Grand Totals */}
      {filtered.length > 0 && (
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-border/40">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4 text-center">TOTAL GENERAL</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Transacciones</p>
                <p className="text-3xl font-black text-foreground">{totals.count}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Monto Base</p>
                <p className="text-3xl font-black text-muted-foreground">{formatCurrency(totals.baseAmount)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Descuentos</p>
                <p className="text-3xl font-black text-primary">-{formatCurrency(totals.discountAmount)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Total Final</p>
                <p className="text-3xl font-black text-accent">{formatCurrency(totals.finalPrice)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
